import React, { useEffect, useRef, useState } from 'react';

export default function AnimatedBackground() {
  const orbOneRef = useRef(null);
  const orbTwoRef = useRef(null);
  const orbThreeRef = useRef(null);
  const rafRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const [isReducedMotion, setIsReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setIsReducedMotion(media.matches);

    updateMotion();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', updateMotion);

      return () => {
        media.removeEventListener('change', updateMotion);
      };
    }

    media.addListener(updateMotion);
    return () => {
      media.removeListener(updateMotion);
    };
  }, []);

  useEffect(() => {
    if (isReducedMotion) {
      return undefined;
    }

    const render = () => {
      const { x, y } = pointerRef.current;

      if (orbOneRef.current) {
        orbOneRef.current.style.transform = `translate3d(${x * 0.02}px, ${y * 0.02}px, 0)`;
      }

      if (orbTwoRef.current) {
        orbTwoRef.current.style.transform = `translate3d(${-x * 0.015}px, ${y * 0.025}px, 0)`;
      }

      if (orbThreeRef.current) {
        orbThreeRef.current.style.transform = `translate3d(-50%, ${-y * 0.01}px, 0)`;
      }

      rafRef.current = null;
    };

    const handleMouseMove = (event) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };

      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(render);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isReducedMotion]);

  return (
    <>
      <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div
          ref={orbOneRef}
          className={`absolute w-[520px] h-[520px] sm:w-[600px] sm:h-[600px] bg-purple-600 rounded-full mix-blend-screen blur-[100px] sm:blur-[120px] opacity-30 ${isReducedMotion ? '' : 'animate-pulse'}`}
        />
        <div
          ref={orbTwoRef}
          className={`absolute right-0 top-0 w-[420px] h-[420px] sm:w-[500px] sm:h-[500px] bg-cyan-500 rounded-full mix-blend-screen blur-[100px] sm:blur-[120px] opacity-25 ${isReducedMotion ? '' : 'animate-pulse'}`}
          style={!isReducedMotion ? { animationDelay: '1s' } : undefined}
        />
        <div
          ref={orbThreeRef}
          className={`absolute left-1/2 bottom-0 w-[460px] h-[460px] sm:w-[550px] sm:h-[550px] bg-pink-500 rounded-full mix-blend-screen blur-[100px] sm:blur-[120px] opacity-20 ${isReducedMotion ? '' : 'animate-pulse'}`}
          style={!isReducedMotion ? { transform: 'translate3d(-50%, 0, 0)', animationDelay: '2s' } : { transform: 'translateX(-50%)' }}
        />
      </div>

      <div
        className="fixed inset-0 pointer-events-none opacity-20"
        aria-hidden="true"
        style={{
          backgroundImage:
            'linear-gradient(rgba(147, 51, 234, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(147, 51, 234, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />
    </>
  );
}
